import PropTypes from "prop-types";
import { memo, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import {
  formatUtcDay,
  sectionTranslator,
  useIntensity,
} from "./readinessFormat";

const PAGE_SIZE = 60;
const DASH = "—";

const tr = sectionTranslator("table");

const answerTone = (flag) => {
  if (flag === "lower") return "amber";
  return flag === "higher" ? "green" : "none";
};

const scoreTone = (status) => {
  if (status === "below") return "amber";
  return status === "above" ? "green" : "none";
};

/**
 * Renders a questionnaire row for the readiness daily table.
 * @param {*} day
 * @param {*} columns
 * @param {*} hrvSourceName
 * @returns
 */
const QuestionnaireRow = ({ day, columns, hrvSourceName }) => {
  const { t } = useTranslation("athlete-portal");
  const { toMeasure } = useIntensity();
  const hrvSourceSuffix =
    columns.showHrvSource && day.hrvSource ? ` (${hrvSourceName})` : "";
  const hrvText = day.hrv == null ? DASH : `${day.hrv}${hrvSourceSuffix}`;
  return (
    <tr>
      <td>{formatUtcDay(day.utcDate)}</td>
      {day.answers.map((answer) => (
        <td key={answer.key}>
          <span className={`rr-cell rr-cell--${answerTone(answer.flag)}`}>
            {answer.value}
          </span>
        </td>
      ))}
      <td>
        <span
          className={`rr-cell rr-cell--strong rr-cell--${scoreTone(day.status)}`}
        >
          {day.total}
        </span>
      </td>
      <td>
        <span className={`rr-status rr-status--${day.status}`}>
          {t(`readinessRate.report.status.${day.status}`)}
        </span>
      </td>
      <td>{toMeasure(day.sessionRpe) ?? DASH}</td>
      {columns.hrv ? <td>{hrvText}</td> : null}
      {columns.restingHr ? <td>{day.restingHr ?? DASH}</td> : null}
    </tr>
  );
};

QuestionnaireRow.propTypes = {
  day: PropTypes.object.isRequired,
  columns: PropTypes.object.isRequired,
  hrvSourceName: PropTypes.string,
};

/**
 * Renders a missed row for the readiness daily table.
 * @param {*} day
 * @param {*} columnCount
 * @returns
 */
const MissedRow = ({ day, columnCount }) => {
  const { t } = useTranslation("athlete-portal");
  return (
    <tr className="rr-table__missed">
      <td>{formatUtcDay(day.utcDate)}</td>
      <td colSpan={columnCount - 1}>
        <span className="rr-status rr-status--missed">
          {tr(t, "missedPill")}
        </span>
        <span className="rr-table__missed-text">{tr(t, "missedText")}</span>
      </td>
    </tr>
  );
};

MissedRow.propTypes = {
  day: PropTypes.object.isRequired,
  columnCount: PropTypes.number.isRequired,
};

// The day-by-day log, newest first: each answer tinted against that day's own normal, the score
// and its status, and what the day had for Session RPE, HRV and Resting HR. A missed training
// day is a row of its own. HRV and Resting HR only get a column when the client has that data.
export const ReadinessDailyTable = memo(({ report, athleteName }) => {
  const { t } = useTranslation("athlete-portal");
  const { context } = useIntensity();
  const [limit, setLimit] = useState(PAGE_SIZE);
  const rows = useMemo(() => [...report.days].reverse(), [report.days]);
  const columns = {
    hrv: report.overlays.hrv.length > 0,
    restingHr: report.overlays.restingHr.length > 0,
    showHrvSource: report.overlays.hrvSources.length > 1,
  };
  const columnCount = 9 + Number(columns.hrv) + Number(columns.restingHr);
  const hidden = rows.length - limit;
  const owner = athleteName ? { name: athleteName } : null;

  return (
    <div className="rr-table-block">
      <div className="rr-table-wrap">
        <table className="rr-table">
          <thead>
            <tr>
              <th>{t("reports.date")}</th>
              <th>{t("readinessRate.sore")}</th>
              <th>{t("readinessRate.stress")}</th>
              <th>{t("readinessRate.energy")}</th>
              <th>{t("readinessRate.mood")}</th>
              <th>{t("readinessRate.sleep")}</th>
              <th>{t("readinessRate.score")}</th>
              <th>{tr(t, "vsNormal")}</th>
              <th>{tr(t, "sessionRpe", { context })}</th>
              {columns.hrv ? <th>{tr(t, "hrv")}</th> : null}
              {columns.restingHr ? <th>{tr(t, "restingHr")}</th> : null}
            </tr>
          </thead>
          <tbody>
            {rows
              .slice(0, limit)
              .map((day) =>
                day.kind === "missed" ? (
                  <MissedRow
                    key={day.utcDate}
                    day={day}
                    columnCount={columnCount}
                  />
                ) : (
                  <QuestionnaireRow
                    key={day.utcDate}
                    day={day}
                    columns={columns}
                    hrvSourceName={
                      day.hrvSource
                        ? t(
                            `readinessRate.report.chart.tooltip.source.${day.hrvSource}`
                          )
                        : ""
                    }
                  />
                )
              )}
          </tbody>
        </table>
      </div>
      {hidden > 0 ? (
        <button
          type="button"
          className="rr-table__more"
          data-button-type="secondary"
          onClick={() => setLimit((current) => current + PAGE_SIZE)}
        >
          {tr(t, "showMore", { count: Math.min(hidden, PAGE_SIZE) })}
        </button>
      ) : null}
      <p className="rr-table__footnote">
        {owner ? tr(t, "footnote", owner) : tr(t, "footnoteSelf")}
      </p>
    </div>
  );
});

ReadinessDailyTable.propTypes = {
  report: PropTypes.shape({
    days: PropTypes.array.isRequired,
    overlays: PropTypes.object.isRequired,
  }).isRequired,
  athleteName: PropTypes.string,
};
